/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/arcana.tsx
   All Arcana domain cards with text pre-rendered as JSX.
   Edit the `rendered` field to update display content.
   Edit the `text` field to update search index content.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  STRESS,
  HOPE,
  SPELLCAST_ROLL,
  MAGIC_DAMAGE,
  FAR,
  MELEE,
  VERY_CLOSE,
  CLOSE,
  VERY_FAR,
  LONG_REST,
  VAULT,
} from '../../templates';

const arcanaCards: DomainCard[] = [
  {
    name: "Rune Ward",
    level: 1,
    domain: "Arcana",
    type: "Spell",
    recallCost: 0,
    text: "You have a deeply personal trinket that can be infused with protective magic and held as a ward by you or an ally. Describe what it is and why it's important to you. The ward's holder can spend a Hope to reduce incoming damage by **1d8**.\n\nIf the Ward Die result is 8, the ward's power ends after it reduces damage this turn. It can be recharged for free on your next rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You have a deeply personal trinket that can be infused with protective magic and held as a ward by you or an ally. Describe what it is and why it's important to you. The ward's holder can spend a {HOPE} to reduce incoming damage by <strong className="inline-strong">1d8</strong>.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">If the Ward Die result is 8, the ward's power ends after it reduces damage this turn. It can be recharged for free on your next rest.</p>
      </>
    ),
  },
  {
    name: "Unleash Chaos",
    level: 1,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "At the beginning of a session, place a number of tokens equal to your Spellcast trait on this card.\n\nMake a **Spellcast Roll** against a target within Far range and spend any number of tokens to channel raw energy from within yourself to unleash against them. On a success, roll a number of **d10s** equal to the tokens you spent and deal that much magic damage to the target. **Mark a Stress** to replenish this card with tokens (up to your Spellcast trait).\n\nAt the end of each session, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">At the beginning of a session, place a number of tokens equal to your Spellcast trait on this card.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {FAR} range and spend any number of tokens to channel raw energy from within yourself to unleash against them. On a success, roll a number of <strong className="inline-strong">d10s</strong> equal to the tokens you spent and deal that much {MAGIC_DAMAGE} to the target. <strong className="inline-strong">Mark a {STRESS}</strong> to replenish this card with tokens (up to your Spellcast trait).</p>
        <div key={3} className="card-text-gap" />
        <p key={4} className="card-text-para">At the end of each session, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Wall Walk",
    level: 1,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "**Spend a Hope** to allow a creature you can touch to climb on walls and ceilings as easily as walking on the ground. This lasts until the end of the scene or you cast Wall Walk again",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to allow a creature you can touch to climb on walls and ceilings as easily as walking on the ground. This lasts until the end of the scene or you cast Wall Walk again</p>
      </>
    ),
  },
  {
    name: "Cinder Grasp",
    level: 2,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against a target within Melee range. On a success, the target instantly bursts into flames, takes **1d20+3** magic damage, and is temporarily lit _On Fire_.\n\nWhen a creature acts while _On Fire_, they must take an extra **2d6** magic damage if they are still _On Fire_ at the end of their action.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {MELEE} range. On a success, the target instantly bursts into flames, takes <strong className="inline-strong">1d20+3</strong> {MAGIC_DAMAGE}, and is temporarily lit <em>On Fire</em>.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When a creature acts while <em>On Fire</em>, they must take an extra <strong className="inline-strong">2d6</strong> {MAGIC_DAMAGE} if they are still <em>On Fire</em> at the end of their action.</p>
      </>
    ),
  },
  {
    name: "Floating Eye",
    level: 2,
    domain: "Arcana",
    type: "Spell",
    recallCost: 0,
    text: "**Spend a Hope** to create a single, small floating orb that you can move anywhere within Very Far range. While this spell is active, you can see through the orb as though you're looking out from its position. You can transition between using your own senses and seeing through the orb freely. If the orb takes damage or moves out of range, the spell ends.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to create a single, small floating orb that you can move anywhere within {VERY_FAR} range. While this spell is active, you can see through the orb as though you're looking out from its position. You can transition between using your own senses and seeing through the orb freely. If the orb takes damage or moves out of range, the spell ends.</p>
      </>
    ),
  },
  {
    name: "Counterspell",
    level: 3,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "You can interrupt a magical effect taking place by making a reaction roll using your Spellcast trait. On a success, the effect stops and any consequences are avoided, and this card is placed in your vault.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can interrupt a magical effect taking place by making a reaction roll using your Spellcast trait. On a success, the effect stops and any consequences are avoided, and this card is placed in your {VAULT}.</p>
      </>
    ),
  },
  {
    name: "Flight",
    level: 3,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (15)**. On a success, place a number of tokens equal to your Agility on this card (minimum 1). When you make an action roll while flying, spend a token from this card. After the action that spends the last token is resolved, you descend to the ground directly below you.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (15). On a success, place a number of tokens equal to your Agility on this card (minimum 1). When you make an action roll while flying, spend a token from this card. After the action that spends the last token is resolved, you descend to the ground directly below you.</p>
      </>
    ),
  },
  {
    name: "Blink Out",
    level: 4,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (12)**. On a success, **spend a Hope** to teleport to another point you can see within Far range. If any willing creatures are within Very Close range, **spend an additional Hope** for each creature to bring them with you.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (12). On a success, <strong className="inline-strong">spend a {HOPE}</strong> to teleport to another point you can see within {FAR} range. If any willing creatures are within {VERY_CLOSE} range, <strong className="inline-strong">spend an additional {HOPE}</strong> for each creature to bring them with you.</p>
      </>
    ),
  },
  {
    name: "Preservation Blast",
    level: 4,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll** against all targets within Melee range. Targets you succeed against are forced back to Far range and take **d8+3** magic damage using your Spellcast trait.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against all targets within {MELEE} range. Targets you succeed against are forced back to {FAR} range and take <strong className="inline-strong">d8+3</strong> {MAGIC_DAMAGE} using your Spellcast trait.</p>
      </>
    ),
  },
  {
    name: "Chain Lightning",
    level: 5,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "**Mark 2 Stress** to make a **Spellcast Roll**, unleashing lightning on all targets within Close range. Targets you succeed against must make a reaction roll with a Difficulty equal to the result of your Spellcast Roll. Targets who fail take **2d8+4** magic damage. Additional adversaries not already targeted by Chain Lightning and within Close range of previous targets who took damage must also make the reaction roll. Targets who fail take **2d8+4** magic damage. This chain continues until there are no more adversaries within range.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Mark 2 {STRESS}</strong> to make a {SPELLCAST_ROLL}, unleashing lightning on all targets within {CLOSE} range. Targets you succeed against must make a reaction roll with a Difficulty equal to the result of your Spellcast Roll. Targets who fail take <strong className="inline-strong">2d8+4</strong> {MAGIC_DAMAGE}. Additional adversaries not already targeted by Chain Lightning and within {CLOSE} range of previous targets who took damage must also make the reaction roll. Targets who fail take <strong className="inline-strong">2d8+4</strong> {MAGIC_DAMAGE}. This chain continues until there are no more adversaries within range.</p>
      </>
    ),
  },
  {
    name: "Premonition",
    level: 5,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "You can channel arcane energy to have visions of the future. Once per long rest, immediately after the GM conveys the consequences of a roll you made, you can rescind the move and consequences like they never happened and make another move instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can channel arcane energy to have visions of the future. Once per {LONG_REST}, immediately after the GM conveys the consequences of a roll you made, you can rescind the move and consequences like they never happened and make another move instead.</p>
      </>
    ),
  },
  {
    name: "Rift Walker",
    level: 6,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (15)**. On a success, you place an arcane marking on the ground where you currently stand. The next time you successfully cast Rift Walker, a rift in space opens up, providing safe passage back to the exact spot where the marking was placed. This rift stays open until you choose to close it or you cast another spell.\n\nYou can drop the spell at any time to cast Rift Walker again and place the marking somewhere new.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (15). On a success, you place an arcane marking on the ground where you currently stand. The next time you successfully cast Rift Walker, a rift in space opens up, providing safe passage back to the exact spot where the marking was placed. This rift stays open until you choose to close it or you cast another spell.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can drop the spell at any time to cast Rift Walker again and place the marking somewhere new.</p>
      </>
    ),
  },
  {
    name: "Telekinesis",
    level: 6,
    domain: "Arcana",
    type: "Spell",
    recallCost: 0,
    text: "Make a **Spellcast Roll** against a target within Far range. On a success, you can use your mind to move them anywhere within Far range of their original position. You can throw the lifted target as an attack by making an additional Spellcast Roll against the second target you're trying to attack. On a success, deal **d12+4** physical damage to the second target using your Proficiency. This spell then ends.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {FAR} range. On a success, you can use your mind to move them anywhere within {FAR} range of their original position. You can throw the lifted target as an attack by making an additional Spellcast Roll against the second target you're trying to attack. On a success, deal <strong className="inline-strong">d12+4</strong> physical damage to the second target using your Proficiency. This spell then ends.</p>
      </>
    ),
  },
  {
    name: "Arcana-Touched",
    level: 7,
    domain: "Arcana",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Arcana domain, gain the following benefits:\n\n- +1 bonus to your Spellcast Rolls\n- Once per rest, you can switch the results of your Hope and Fear Dice.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your loadout are from the Arcana domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+1 bonus to your Spellcast Rolls</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Once per rest, you can switch the results of your Hope and Fear Dice.</span></div>
      </>
    ),
  },
  {
    name: "Cloaking Blast",
    level: 7,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "When you make a successful **Spellcast Roll** to cast a different spell, you can **spend a Hope** to become _Cloaked_. While _Cloaked_, you remain unseen if you are stationary when an adversary moves to where they would normally see you. When you move into or within an adversary's line of sight or make an attack, you are no longer _Cloaked_.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a successful {SPELLCAST_ROLL} to cast a different spell, you can <strong className="inline-strong">spend a {HOPE}</strong> to become <em>Cloaked</em>. While <em>Cloaked</em>, you remain unseen if you are stationary when an adversary moves to where they would normally see you. When you move into or within an adversary's line of sight or make an attack, you are no longer <em>Cloaked</em>.</p>
      </>
    ),
  },
  {
    name: "Arcane Reflection",
    level: 8,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "When you would take magic damage, you can **spend any number of Hope** to roll that many **d6s**. If any roll a 6, the attack is reflected back to the caster, dealing the damage to them instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you would take {MAGIC_DAMAGE}, you can <strong className="inline-strong">spend any number of {HOPE}</strong> to roll that many <strong className="inline-strong">d6s</strong>. If any roll a 6, the attack is reflected back to the caster, dealing the damage to them instead.</p>
      </>
    ),
  },
  {
    name: "Confusing Aura",
    level: 8,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (14)**. Once per long rest on a success, you create a layer of illusion over your body that makes it hard to tell exactly where you are. **Mark any number of Stress** to make that many additional layers. When an adversary makes an attack against you, roll a number of **d6s** equal to the number of layers currently active. If any roll a 5 or higher, one layer of the aura is destroyed and the attack fails. If all the results are 4 or lower, you take the damage and this spell ends",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (14). Once per {LONG_REST} on a success, you create a layer of illusion over your body that makes it hard to tell exactly where you are. <strong className="inline-strong">Mark any number of {STRESS}</strong> to make that many additional layers. When an adversary makes an attack against you, roll a number of <strong className="inline-strong">d6s</strong> equal to the number of layers currently active. If any roll a 5 or higher, one layer of the aura is destroyed and the attack fails. If all the results are 4 or lower, you take the damage and this spell ends</p>
      </>
    ),
  },
  {
    name: "Earthquake",
    level: 9,
    domain: "Arcana",
    type: "Spell",
    recallCost: 2,
    text: "Make a **Spellcast Roll (16)**. Once per rest on a success, all targets within Very Far range who aren't flying must make a Reaction Roll (18). Targets who fail take **3d10+8** physical damage and are temporarily _Vulnerable_. Targets who succeed take half damage.\n\nAdditionally, when you succeed on the Spellcast Roll, all terrain within Very Far range becomes dicult to move through and structures within this range might sustain damage or crumble.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (16). Once per rest on a success, all targets within {VERY_FAR} range who aren't flying must make a Reaction Roll (18). Targets who fail take <strong className="inline-strong">3d10+8</strong> physical damage and are temporarily <em>Vulnerable</em>. Targets who succeed take half damage.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, when you succeed on the Spellcast Roll, all terrain within {VERY_FAR} range becomes dicult to move through and structures within this range might sustain damage or crumble.</p>
      </>
    ),
  },
  {
    name: "Sensory Projection",
    level: 9,
    domain: "Arcana",
    type: "Spell",
    recallCost: 0,
    text: "Once per rest, make a **Spellcast Roll (15)**. On a success, drop into a vision that lets you clearly see and hear any place you have been before as though you are standing there in this moment. You can move freely in this vision and are not constrained by the physics or impediments of a physical body. This spell cannot be detected by mundane or magical means. You drop out of this vision upon taking damage or casting another spell.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, make a {SPELLCAST_ROLL} (15). On a success, drop into a vision that lets you clearly see and hear any place you have been before as though you are standing there in this moment. You can move freely in this vision and are not constrained by the physics or impediments of a physical body. This spell cannot be detected by mundane or magical means. You drop out of this vision upon taking damage or casting another spell.</p>
      </>
    ),
  },
  {
    name: "Adjust Reality",
    level: 10,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "After you or a willing ally make any roll, you can **spend 5 Hope** to change the numerical result of that roll to a result of your choice instead. The result must be plausible within the range of the dice.",
    rendered: (
      <>
        <p key={0} className="card-text-para">After you or a willing ally make any roll, you can <strong className="inline-strong">spend 5 {HOPE}</strong> to change the numerical result of that roll to a result of your choice instead. The result must be plausible within the range of the dice.</p>
      </>
    ),
  },
  {
    name: "Falling Sky",
    level: 10,
    domain: "Arcana",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against all adversaries within Far range. **Mark any number of Stress** to make shards of arcana rain down from above. Targets you succeed against take **1d20+2** magic damage for each Stress marked.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against all adversaries within {FAR} range. <strong className="inline-strong">Mark any number of {STRESS}</strong> to make shards of arcana rain down from above. Targets you succeed against take <strong className="inline-strong">1d20+2</strong> {MAGIC_DAMAGE} for each Stress marked.</p>
      </>
    ),
  },
];

export default arcanaCards;
